/**
 * @metabus/esign-client — the browser client for buildgravity-dialer's
 * e-signing API.
 *
 * Fetch-based, sends the Firebase ID token as `Authorization: Bearer` — the
 * SAME auth model @metabus/dialer-sdk's real client uses (a caller-identity
 * gate; the backend's `get_documenso_caller` seam admits an
 * `is_super_admin=true` Firebase user or an allow-listed service account,
 * per buildgravity-dialer/docs/documenso-api-contract.md "Auth model"). This
 * is deliberately NOT the OIDC/ADC service-to-service flow
 * @metabus/dialer-client uses — see this package's README "Package split
 * decision" for why that precedent does not apply here.
 */

import { EsignError, bodyExcerpt } from './errors.js';
import type {
  ListSignatureRequestsParams,
  ListSignatureRequestsResult,
  SendForSignatureInput,
  SendForSignatureResult,
  SignatureRequestStatus,
} from './types.js';

export { EsignError } from './errors.js';
export type { EsignErrorKind, EsignErrorOpts } from './errors.js';

/** Returns the current Firebase ID token, or null/undefined when signed out. */
export type EsignTokenProvider = () => Promise<string | null | undefined>;

export interface CreateEsignClientOptions {
  /** Dialer API base URL (e.g. https://dialer-api-kaeqtzcrba-uc.a.run.app). */
  baseUrl: string;
  /** Supplies the Firebase ID token sent as `Authorization: Bearer`. */
  getToken: EsignTokenProvider;
  /** Optional fetch override (tests inject a mock). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export interface EsignClient {
  /**
   * `POST /api/documenso/signature-requests` — send a PDF for signature.
   * Returns `{success:false, error}` (still HTTP 200) on a Documenso-side
   * failure; throws EsignError on a genuine client input error (HTTP 400)
   * or any transport failure.
   */
  sendForSignature(input: SendForSignatureInput): Promise<SendForSignatureResult>;
  /**
   * `GET /api/documenso/signature-requests/{trackingId}` — live status
   * lookup. Throws `EsignError(kind:'not_found')` for an unknown
   * tracking_id. A failed live Documenso refresh does NOT throw — the
   * dialer serves the last-known cached record with
   * `live_refresh_failed: true` instead (contract doc).
   */
  getSignatureStatus(trackingId: string): Promise<SignatureRequestStatus>;
  /**
   * `GET /api/documenso/signature-requests` — the list/history endpoint
   * (contract doc AC3). Backed by the dialer's own cached record store, no
   * per-row live Documenso call.
   */
  listSignatureRequests(
    params?: ListSignatureRequestsParams,
  ): Promise<ListSignatureRequestsResult>;
}

async function safeText(resp: Response): Promise<string> {
  try {
    return await resp.text();
  } catch {
    return '';
  }
}

export function createEsignClient(opts: CreateEsignClientOptions): EsignClient {
  const baseUrl = opts.baseUrl;
  const doFetch = opts.fetchImpl ?? fetch;

  async function authHeaders(): Promise<Record<string, string>> {
    const token = await opts.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async function doGet<T>(path: string): Promise<T> {
    const headers = await authHeaders();
    let resp: Response;
    try {
      resp = await doFetch(`${baseUrl}${path}`, { method: 'GET', headers });
    } catch (cause) {
      throw new EsignError({
        kind: 'network_error',
        message: `Network error calling dialer GET ${path}: ${
          cause instanceof Error ? cause.message : String(cause)
        }`,
        cause,
      });
    }

    if (resp.status === 404) {
      const detail = await safeText(resp);
      throw new EsignError({
        kind: 'not_found',
        message: `Dialer has no signing request at ${path}`,
        status: 404,
        detail: bodyExcerpt(detail),
      });
    }

    if (!resp.ok) {
      const detail = await safeText(resp);
      throw new EsignError({
        kind: 'http_error',
        message: `Dialer responded ${resp.status} on GET ${path}: ${bodyExcerpt(detail)}`,
        status: resp.status,
        detail: bodyExcerpt(detail),
      });
    }

    try {
      return (await resp.json()) as T;
    } catch (cause) {
      throw new EsignError({
        kind: 'parse_error',
        message: `Dialer returned ${resp.status} but body was not valid JSON on GET ${path}`,
        status: resp.status,
        cause,
      });
    }
  }

  return {
    async sendForSignature({ file, filename, metadata }) {
      const form = new FormData();
      const partName = filename ?? (file instanceof File ? file.name : 'document.pdf');
      form.append('file', file, partName);
      form.append('metadata', JSON.stringify(metadata));

      const headers = await authHeaders();
      // Do NOT set Content-Type here — fetch derives the multipart boundary
      // from the FormData body; a manual header would drop it.
      let resp: Response;
      try {
        resp = await doFetch(`${baseUrl}/api/documenso/signature-requests`, {
          method: 'POST',
          headers,
          body: form,
        });
      } catch (cause) {
        throw new EsignError({
          kind: 'network_error',
          message: `Network error calling dialer POST /api/documenso/signature-requests: ${
            cause instanceof Error ? cause.message : String(cause)
          }`,
          cause,
        });
      }

      // A genuine client input error (non-PDF upload, malformed metadata) is
      // a real HTTP 400; a Documenso-side failure is still HTTP 200 with
      // {success:false, error} in the body (contract doc).
      if (!resp.ok) {
        const detail = await safeText(resp);
        throw new EsignError({
          kind: 'http_error',
          message: `Dialer responded ${resp.status} on POST /api/documenso/signature-requests: ${bodyExcerpt(detail)}`,
          status: resp.status,
          detail: bodyExcerpt(detail),
        });
      }

      try {
        return (await resp.json()) as SendForSignatureResult;
      } catch (cause) {
        throw new EsignError({
          kind: 'parse_error',
          message: 'Dialer returned 200 but body was not valid JSON on POST /api/documenso/signature-requests',
          status: resp.status,
          cause,
        });
      }
    },

    async getSignatureStatus(trackingId: string) {
      return doGet<SignatureRequestStatus>(
        `/api/documenso/signature-requests/${encodeURIComponent(trackingId)}`,
      );
    },

    async listSignatureRequests(params) {
      const q = new URLSearchParams();
      if (params?.status) q.set('status', params.status);
      if (params?.limit != null) q.set('limit', String(params.limit));
      if (params?.offset != null) q.set('offset', String(params.offset));
      const qs = q.toString();
      return doGet<ListSignatureRequestsResult>(
        `/api/documenso/signature-requests${qs ? `?${qs}` : ''}`,
      );
    },
  };
}
